import React from "react";
import { useSearchParams } from "react-router-dom";
import { REPORT_SPA_QUERY_PARAMS } from "@core/reports/constants/reportSpaQueryParams";
import { LAYOUT_DEFAULT } from "@features/presentationLayout/types/presentationLayout";
import {
  isReportTab,
  lensKeyForTab,
  queryKeysForTab,
  reportTabsForScope,
  type ReportEntityScope,
  type ReportTab,
} from "@core/reports/utils/reportSectionOrigin";
import {
  LAYOUT_EXCLUDED_VIEWS,
  readSectionLens,
  writeSectionLens,
} from "@core/reports/utils/reportSectionViewLens";

export function activeReportTab(scope: ReportEntityScope, params: URLSearchParams): ReportTab {
  if (scope === "church") return "church";
  const raw = params.get(REPORT_SPA_QUERY_PARAMS.SECTION);
  const allowed = reportTabsForScope(scope);
  if (raw && isReportTab(raw) && allowed.includes(raw)) return raw;
  return allowed[0];
}

/**
 * The layout value the URL should hold, or null when it is already right.
 * A missing param means Default. The server answered with a different layout → that one.
 */
export function layoutParamCorrection(requested: string | null, currentLayout?: string | null): string | null {
  const asked = requested || LAYOUT_DEFAULT;
  if (currentLayout && currentLayout !== asked) return currentLayout;
  return null;
}

/** Params with the active tab's layout set. Default omits the param. A named layout resets that tab's excluded Views. */
export function withLayout(params: URLSearchParams, layout: string, scope: ReportEntityScope = "church"): URLSearchParams {
  const next = new URLSearchParams(params);
  const tab = activeReportTab(scope, next);
  const key = queryKeysForTab(scope, tab).layout;
  if (layout === LAYOUT_DEFAULT) next.delete(key);
  else next.set(key, layout);
  if (layout !== LAYOUT_DEFAULT) {
    const lensKey = lensKeyForTab(scope, tab);
    if (LAYOUT_EXCLUDED_VIEWS.includes(readSectionLens(next, lensKey, scope).view)) {
      writeSectionLens(next, lensKey, { view: "default" }, scope);
    }
  }
  return next;
}

export function readRequestedLayout(params: URLSearchParams, scope: ReportEntityScope = "church"): string {
  const tab = activeReportTab(scope, params);
  return params.get(queryKeysForTab(scope, tab).layout) || LAYOUT_DEFAULT;
}

/** The corrected params when a named layout has an excluded View, else null. */
export function layoutUrlCorrection(
  params: URLSearchParams,
  scope: ReportEntityScope = "church"
): URLSearchParams | null {
  const next = withLayout(params, readRequestedLayout(params, scope), scope);
  return next.toString() === params.toString() ? null : next;
}

/**
 * Keeps the active tab's layout consistent with its View. Corrections replace the history entry so Back skips
 * them; user choices push a new entry.
 */
export function useReportLayoutParam(enabled: boolean, scope: ReportEntityScope = "church") {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedLayout = readRequestedLayout(searchParams, scope);
  const correction = React.useMemo(() => layoutUrlCorrection(searchParams, scope), [searchParams, scope]);

  const setSearchParamsRef = React.useRef(setSearchParams);
  React.useLayoutEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  const setLayout = React.useCallback(
    (layout: string, replace = false) => {
      setSearchParamsRef.current((prev) => withLayout(prev, layout, scope), { replace });
    },
    [scope]
  );

  React.useEffect(() => {
    if (enabled && correction) setSearchParams(correction, { replace: true });
  }, [enabled, correction, setSearchParams]);

  return { requestedLayout, setLayout, correcting: correction !== null };
}
